import { PAYMENT_MODE_BANK, PAYMENT_MODE_INVOICE } from '@/utils/payment';

const shouldRedirectToPayment = (cartStore: ReturnType<typeof useCartStore>) => {
  const selectedPaymentModeNotAllowed = cartStore.getPaymentModes.some(
    (pm) => pm.code === cartStore.getPaymentMode && !pm.allowed,
  );

  const isAppleGooglePayPaymentMode = [PAYMENT_MODE_ADYEN_APPLEPAY, PAYMENT_MODE_ADYEN_GOOGLEPAY].includes(
    cartStore.getPaymentMode,
  );

  // Check if payment mode is allowed for the checkout.
  return (
    (import.meta.server && selectedPaymentModeNotAllowed && !isAppleGooglePayPaymentMode) ||
    (import.meta.client && selectedPaymentModeNotAllowed && isAppleGooglePayPaymentMode)
  );
};

export default defineNuxtRouteMiddleware(async (to) => {
  const { runWithContext } = useNuxtApp();
  const cartStore = useCartStore();
  const userStore = useUserStore();
  const { query } = to;

  /*
   * This has to run before the Promise.all as the checkAppleAndGooglePayEligibility()
   * result is needed for the cartStore.loadPaymentModes() api call.
   */
  await checkAppleAndGooglePayEligibility();

  await Promise.all([
    runWithContext(() => cartStore.loadCart({ fields: 'FULL' })),
    runWithContext(() => cartStore.loadPaymentModes()),
  ]);

  return runWithContext(async () => {
    if (shouldRedirectToPayment(cartStore)) {
      return navigateToWithAdobeParam('/checkout/payment/', query);
    }

    if (!cartStore.getReachedMinimumOrderValue || cartStore.getHasNoStockEntries) {
      return navigateToWithAdobeParam('/cart/', query);
    }

    if (!cartStore.getPhoneNumberIsValid) {
      return navigateToWithAdobeParam('/checkout/address/', query);
    }

    if (!cartStore.getBillingAddress) {
      return navigateToWithAdobeParam('/checkout/address/', query);
    }

    if (!cartStore.getDeliveryAddress) {
      return navigateToWithAdobeParam('/checkout/address/', query);
    }

    if (cartStore.getDeliveryAddressIsPickupStore) {
      try {
        await userStore.verifyAddress(cartStore.getDeliveryAddress);
      } catch (error) {
        return runWithContext(() =>
          navigateToWithAdobeParam('/checkout/address/', { ...query, error: 'invalidPickupStation' }),
        );
      }
    }

    if (
      (!cartStore.getPaymentMode && !cartStore.getFullPaybackPayment) ||
      ([PAYMENT_MODE_BANK, PAYMENT_MODE_INVOICE].includes(cartStore.getPaymentMode) && !cartStore.getBirthdayAdult)
    ) {
      return runWithContext(() => navigateToWithAdobeParam('/checkout/payment/', query));
    }
  });
});
